/**
 * KPI Library Page (Phase 24.1, extended Phase 24.7 / Track 1)
 *
 * Lists org-owned KPI definitions with cards, search, category filter.
 * Empty state prompts user to create their first KPI.
 * "Create KPI" button opens the AI Builder modal.
 *
 * Phase 24.7 (Track 1):
 *  - Cards display scope (zones + positions) on the read view
 *  - Q11 stale-zone badge surfaces when any scoped zone is `isActive=false`
 *  - "All-deactivated" pause badge when every scoped zone is inactive AND the
 *    KPI is currently disabled (cascade in Chunk 6 will trip this)
 *  - Inline edit form gains zone + position chip pickers backed by
 *    `useUpdateKpiScope` (PUT /api/kpi-definitions/:id/scope)
 */

import { useState, useMemo } from 'react';
import {
  Gauge,
  Plus,
  Search,
  Loader2,
  AlertCircle,
  AlertTriangle,
  Pause,
  BarChart3,
  Pencil,
  XCircle,
  Check,
  X,
  MapPin,
} from 'lucide-react';
import {
  useOrgKpiDefinitions,
  useUpdateKpiDefinition,
  useUpdateKpiScope,
  useDisableKpiDefinition,
  type KpiLibraryEntry,
} from '../../hooks/useKpi';
import { useStationLookup } from '../../hooks/useStations';
import { usePositionLookup } from '../../hooks/usePositions';
import { ScopeChipPicker } from '../../components/kpi/ScopeChipPicker';
import { Tooltip } from '../../components/ui/Tooltip';
import { KpiBuilderModal } from './KpiBuilderModal';
import { categoryConfig } from '../../lib/kpi-categories';
import { useDocumentTitle } from '../../lib/hooks/useDocumentTitle';
import type { KpiCategory, KpiDefinition } from '@guild/shared';

export function KpiLibraryPage() {
  useDocumentTitle('KPI Library');

  const [isBuilderOpen, setIsBuilderOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<KpiDefinition>>({});
  // Phase 24.7 (Track 1): scope edit state — separate from definition fields
  // because they hit different endpoints (PUT /:id and PUT /:id/scope).
  const [editScopeStationIds, setEditScopeStationIds] = useState<string[]>([]);
  const [editScopePositionCodes, setEditScopePositionCodes] = useState<string[]>([]);

  const { data: definitions, isLoading, error, refetch } = useOrgKpiDefinitions();
  const updateMutation = useUpdateKpiDefinition();
  const updateScopeMutation = useUpdateKpiScope();
  const disableMutation = useDisableKpiDefinition();
  // Lookups for the edit-form scope pickers. Cached for 5min — cheap.
  const { data: stationLookup } = useStationLookup();
  const { data: positionLookup } = usePositionLookup();

  // Filter definitions by search + category
  const filteredDefinitions = useMemo(() => {
    if (!definitions) return [];
    return definitions.filter(d => {
      const matchesSearch = !searchQuery ||
        d.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        d.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (d.description?.toLowerCase().includes(searchQuery.toLowerCase()));
      const matchesCategory = categoryFilter === 'all' || d.category === categoryFilter;
      return matchesSearch && matchesCategory;
    });
  }, [definitions, searchQuery, categoryFilter]);

  const categories = ['all', 'safety', 'efficiency', 'compliance', 'productivity', 'quality'];

  const startEdit = (def: KpiLibraryEntry) => {
    setEditingId(def.id);
    setEditForm({
      name: def.name,
      description: def.description ?? '',
      category: def.category,
      unit: def.unit,
      displayFormat: def.displayFormat,
    });
    // Seed scope from the resolved server response. Stale (isActive=false)
    // zones are kept in the picker so the trainer can see + remove them.
    setEditScopeStationIds(def.stationIds ?? []);
    setEditScopePositionCodes(def.positionCodes ?? []);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
    setEditScopeStationIds([]);
    setEditScopePositionCodes([]);
  };

  const saveEdit = async (def: KpiLibraryEntry) => {
    // Block scope-empty saves client-side. Backend re-validates via Zod.
    if (editScopeStationIds.length === 0) return;

    // Determine whether definition fields or scope changed (or both).
    const definitionChanged =
      editForm.name !== def.name ||
      (editForm.description ?? '') !== (def.description ?? '') ||
      editForm.category !== def.category ||
      editForm.unit !== def.unit ||
      editForm.displayFormat !== def.displayFormat;

    const currentStationIds = def.stationIds ?? [];
    const currentPositionCodes = def.positionCodes ?? [];
    const scopeChanged =
      !arraysEqual(editScopeStationIds, currentStationIds) ||
      !arraysEqual(editScopePositionCodes, currentPositionCodes);

    try {
      // Run both mutations concurrently when both changed — independent endpoints.
      const ops: Promise<unknown>[] = [];
      if (definitionChanged) {
        ops.push(updateMutation.mutateAsync({ id: def.id, data: editForm }));
      }
      if (scopeChanged) {
        ops.push(
          updateScopeMutation.mutateAsync({
            id: def.id,
            data: {
              stationIds: editScopeStationIds,
              positionCodes: editScopePositionCodes.length > 0 ? editScopePositionCodes : null,
            },
          })
        );
      }
      if (ops.length > 0) await Promise.all(ops);
      cancelEdit();
    } catch {
      // Error surfaces via mutation isError — keep edit form open.
    }
  };

  const handleDisable = async (id: string) => {
    if (!confirm('Disable this KPI? It will no longer be used in new analyses.')) return;
    try {
      await disableMutation.mutateAsync(id);
    } catch {
      // Error handled by mutation state
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 size={32} className="animate-spin text-guild-green" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-red-600">
        <AlertCircle size={32} />
        <p className="mt-2 text-sm">Failed to load KPI definitions</p>
      </div>
    );
  }

  // Lookup options for edit-form pickers. Inactive zones in the existing scope
  // are merged in below so they can be removed from the picker.
  const stationOptionsBase = (stationLookup ?? []).map((s) => ({
    id: s.id,
    name: s.name,
    isActive: true,
  }));
  const positionOptionsBase = (positionLookup ?? []).map((p) => ({
    id: p.code,
    name: p.name,
  }));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">KPI Library</h1>
          <p className="mt-1 text-sm text-gray-500">
            Custom metrics for video-based performance measurement
          </p>
        </div>
        <button
          onClick={() => setIsBuilderOpen(true)}
          className="inline-flex items-center gap-2 rounded-xl bg-guild-green px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-guild-green/25 hover:bg-guild-green/90 transition-all"
        >
          <Plus size={18} />
          Create KPI
        </button>
      </div>

      {/* Filters */}
      {definitions && definitions.length > 0 && (
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name or code..."
              className="w-full rounded-xl border border-gray-200 bg-white pl-10 pr-4 py-2.5 text-sm focus:border-guild-green focus:ring-1 focus:ring-guild-green/30 outline-none"
            />
          </div>

          {/* Category filter */}
          <div className="flex gap-1.5 flex-wrap">
            {categories.map(cat => {
              const isActive = categoryFilter === cat;
              const config = cat !== 'all' ? categoryConfig[cat as KpiCategory] : null;
              return (
                <button
                  key={cat}
                  onClick={() => setCategoryFilter(cat)}
                  className={`
                    inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all
                    ${isActive
                      ? cat === 'all'
                        ? 'bg-gray-900 text-white'
                        : `${config?.bg} ${config?.text} ring-1 ring-current/20`
                      : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                    }
                  `}
                >
                  {cat === 'all' ? 'All' : config?.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Empty state */}
      {definitions && definitions.length === 0 && (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="h-16 w-16 rounded-2xl bg-guild-mint/30 flex items-center justify-center mb-4">
            <Gauge size={32} className="text-guild-green" />
          </div>
          <h2 className="text-lg font-bold text-gray-900">Create your first KPI</h2>
          <p className="mt-2 text-sm text-gray-500 max-w-md">
            Use the AI builder to define custom metrics that will be measured
            automatically from your video footage.
          </p>
          <button
            onClick={() => setIsBuilderOpen(true)}
            className="mt-6 inline-flex items-center gap-2 rounded-xl bg-guild-green px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-guild-green/25 hover:bg-guild-green/90 transition-all"
          >
            <Plus size={18} />
            Create KPI with AI
          </button>
        </div>
      )}

      {/* KPI Cards Grid */}
      {filteredDefinitions.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredDefinitions.map(def => {
            const cat = def.category as KpiCategory | null;
            const catInfo = cat ? categoryConfig[cat] : null;
            const isEditing = editingId === def.id;
            const thresholds = def.defaultThresholds as { warning?: number; critical?: number; target?: number } | null;

            // Phase 24.7 (Track 1): scope summary computed from server-resolved zone metadata.
            const scopedStations = def.stations ?? [];
            const inactiveStations = scopedStations.filter((s) => !s.isActive);
            const activeStations = scopedStations.filter((s) => s.isActive);
            const positionCount = (def.positionCodes ?? []).length;

            // Q11 partial badge: at least one inactive AND at least one still active.
            const showStalePartial = inactiveStations.length > 0 && activeStations.length > 0;
            // Q11 full badge: all stations inactive AND KPI currently disabled.
            // The cascade in Chunk 6 sets isEnabled=false; the badge requires both
            // signals so we don't surface false alarms before the cascade runs.
            const showAllDeactivated =
              scopedStations.length > 0 &&
              activeStations.length === 0 &&
              def.isEnabled === false;

            // Edit-form scope option lists merge inactive (stale) entries from the
            // current selection so the trainer can see + remove them.
            const editStationOptions = isEditing
              ? mergeWithStale(stationOptionsBase, def.stations ?? [], editScopeStationIds)
              : stationOptionsBase;
            // Position lookup doesn't return isActive — assume all active for now.
            // Inactive position codes that linger in selection still display via
            // the chip's id-fallback label.
            const editPositionOptions = positionOptionsBase;

            return (
              <div
                key={def.id}
                className={`
                  rounded-xl border bg-white p-5 space-y-3 transition-all
                  ${!def.isActive ? 'opacity-60 border-gray-200' : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'}
                `}
              >
                {isEditing ? (
                  // Edit mode — definition fields + scope pickers
                  <div className="space-y-3">
                    <input
                      type="text"
                      value={editForm.name ?? ''}
                      onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                      className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-bold focus:border-guild-green focus:ring-1 focus:ring-guild-green/30 outline-none"
                      placeholder="KPI Name"
                    />
                    <textarea
                      value={(editForm.description as string) ?? ''}
                      onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                      className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs resize-none h-16 focus:border-guild-green focus:ring-1 focus:ring-guild-green/30 outline-none"
                      placeholder="Description"
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <select
                        value={(editForm.category as string) ?? ''}
                        onChange={(e) => setEditForm({ ...editForm, category: e.target.value as KpiCategory })}
                        className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs"
                      >
                        <option value="">Category</option>
                        {Object.entries(categoryConfig).map(([key, cfg]) => (
                          <option key={key} value={key}>{cfg.label}</option>
                        ))}
                      </select>
                      <input
                        type="text"
                        value={(editForm.unit as string) ?? ''}
                        onChange={(e) => setEditForm({ ...editForm, unit: e.target.value })}
                        className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs"
                        placeholder="Unit"
                      />
                    </div>

                    {/* Scope pickers (Phase 24.7 / Track 1) */}
                    <div className="rounded-lg bg-gray-50 p-3 space-y-3 border border-gray-100">
                      <ScopeChipPicker
                        label="Zones"
                        options={editStationOptions}
                        selectedIds={editScopeStationIds}
                        onChange={setEditScopeStationIds}
                        required
                        helpText="Required."
                        errorMessage="KPIs must have at least one zone"
                        disabled={updateMutation.isPending || updateScopeMutation.isPending}
                      />
                      <ScopeChipPicker
                        label="Positions"
                        options={editPositionOptions}
                        selectedIds={editScopePositionCodes}
                        onChange={setEditScopePositionCodes}
                        helpText="Optional. Empty = all positions."
                        disabled={updateMutation.isPending || updateScopeMutation.isPending}
                      />
                    </div>

                    {(updateMutation.isError || updateScopeMutation.isError) && (
                      <div className="flex items-center gap-1.5 text-xs text-red-600 bg-red-50 px-2 py-1.5 rounded-md">
                        <AlertCircle size={12} />
                        {(() => {
                          const err = updateScopeMutation.error ?? updateMutation.error;
                          return err instanceof Error ? err.message : 'Save failed';
                        })()}
                      </div>
                    )}

                    <div className="flex gap-2 justify-end">
                      <button
                        onClick={cancelEdit}
                        className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50"
                      >
                        <X size={12} />
                        Cancel
                      </button>
                      <button
                        onClick={() => saveEdit(def)}
                        disabled={
                          updateMutation.isPending ||
                          updateScopeMutation.isPending ||
                          editScopeStationIds.length === 0
                        }
                        className="inline-flex items-center gap-1 rounded-lg bg-guild-green px-3 py-1.5 text-xs font-medium text-white hover:bg-guild-green/90 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {updateMutation.isPending || updateScopeMutation.isPending ? (
                          <Loader2 size={12} className="animate-spin" />
                        ) : (
                          <Check size={12} />
                        )}
                        Save
                      </button>
                    </div>
                  </div>
                ) : (
                  // Display mode
                  <>
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-bold text-gray-900 truncate">{def.name}</h3>
                        <p className="text-xs text-gray-400 font-mono">{def.code}</p>
                      </div>
                      <div className="flex items-center gap-1 ml-2">
                        {def.isActive && (
                          <>
                            <button
                              onClick={() => startEdit(def)}
                              className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
                              title="Edit"
                            >
                              <Pencil size={14} />
                            </button>
                            <button
                              onClick={() => handleDisable(def.id)}
                              className="rounded-lg p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                              title="Disable"
                            >
                              <XCircle size={14} />
                            </button>
                          </>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-1.5">
                      {catInfo && (
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${catInfo.bg} ${catInfo.text}`}>
                          <catInfo.icon size={10} />
                          {catInfo.label}
                        </span>
                      )}
                      {/* Phase 24.7 — Q11 stale-zone badges */}
                      {showAllDeactivated && (
                        <Tooltip
                          content={
                            inactiveStations.length > 0
                              ? `Inactive zones: ${inactiveStations.map((s) => s.name).join(', ')}`
                              : 'No active zones in scope. The KPI is paused until an active zone is added.'
                          }
                        >
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                            <Pause size={10} />
                            All scoped zones deactivated — KPI paused
                          </span>
                        </Tooltip>
                      )}
                      {!showAllDeactivated && showStalePartial && (
                        <Tooltip
                          content={`Inactive zones: ${inactiveStations.map((s) => s.name).join(', ')}`}
                        >
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                            <AlertTriangle size={10} />
                            {inactiveStations.length} zone{inactiveStations.length > 1 ? 's' : ''} deactivated
                          </span>
                        </Tooltip>
                      )}
                    </div>

                    {def.description && (
                      <p className="text-xs text-gray-600 line-clamp-2">{def.description}</p>
                    )}

                    <div className="flex items-center justify-between text-xs">
                      <div className="flex gap-3">
                        {def.unit && (
                          <span className="text-gray-400">
                            Unit: <span className="text-gray-600 font-medium">{def.unit}</span>
                          </span>
                        )}
                        {def.aggregationType && (
                          <span className="text-gray-400">
                            Agg: <span className="text-gray-600 font-medium">{def.aggregationType}</span>
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Thresholds */}
                    {thresholds && (thresholds.warning != null || thresholds.critical != null || thresholds.target != null) && (
                      <div className="flex gap-3 text-xs">
                        {thresholds.target != null && (
                          <span className="text-emerald-600">Target: {thresholds.target}</span>
                        )}
                        {thresholds.warning != null && (
                          <span className="text-amber-600">Warning: {thresholds.warning}</span>
                        )}
                        {thresholds.critical != null && (
                          <span className="text-red-600">Critical: {thresholds.critical}</span>
                        )}
                      </div>
                    )}

                    {/* Phase 24.7 — Scope summary line */}
                    {scopedStations.length > 0 && (
                      <div className="flex items-center gap-1 text-xs text-gray-500">
                        <MapPin size={12} className="flex-shrink-0" />
                        <span className="truncate">
                          Runs on{' '}
                          <span className="font-medium text-gray-700">
                            {scopedStations.length === 1
                              ? scopedStations[0].name
                              : `${scopedStations.length} zones`}
                          </span>
                          {positionCount > 0 && (
                            <span>
                              {' · '}
                              {positionCount} position{positionCount !== 1 ? 's' : ''}
                            </span>
                          )}
                        </span>
                      </div>
                    )}

                    {/* Measurement count + status */}
                    <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                      <div className="flex items-center gap-1.5 text-xs text-gray-400">
                        <BarChart3 size={12} />
                        {def.measurementCount} measurement{def.measurementCount !== 1 ? 's' : ''}
                      </div>
                      {!def.isActive && (
                        <span className="text-xs font-medium text-gray-400 bg-gray-100 px-2 py-0.5 rounded">
                          Disabled
                        </span>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* No results after filtering */}
      {definitions && definitions.length > 0 && filteredDefinitions.length === 0 && (
        <div className="text-center py-12 text-gray-500 text-sm">
          No KPIs match your search or filter criteria.
        </div>
      )}

      {/* Builder Modal */}
      <KpiBuilderModal
        isOpen={isBuilderOpen}
        onClose={() => setIsBuilderOpen(false)}
        onCreated={() => refetch()}
      />
    </div>
  );
}

// ============================================
// Local helpers
// ============================================

function arraysEqual(a: string[], b: string[]) {
  if (a.length !== b.length) return false;
  const aSet = new Set(a);
  for (const v of b) if (!aSet.has(v)) return false;
  return true;
}

/**
 * Merge the lookup-derived options with any "stale" (inactive) zones in the
 * current selection so the trainer can see and remove them via the picker.
 * Inactive options are flagged `isActive: false` — `ScopeChipPicker` keeps
 * them out of the add-dropdown but renders them as removable chips.
 */
function mergeWithStale(
  base: Array<{ id: string; name: string; isActive: boolean }>,
  resolvedStations: Array<{ id: string; name: string; isActive: boolean }>,
  selectedIds: string[]
): Array<{ id: string; name: string; isActive: boolean }> {
  const baseMap = new Map(base.map((o) => [o.id, o]));
  const resolvedMap = new Map(resolvedStations.map((s) => [s.id, s]));
  for (const id of selectedIds) {
    if (baseMap.has(id)) continue;
    const meta = resolvedMap.get(id);
    if (meta) {
      baseMap.set(id, { id: meta.id, name: meta.name, isActive: meta.isActive });
    }
  }
  return Array.from(baseMap.values());
}
